import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Navigation, Menu, User, LogOut, CreditCard, Clock, Copy, CheckCircle, XCircle, Car, Download } from 'lucide-react';
import Footer from '@/components/Footer';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'react-toastify';

const API = '/api';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const MERCADO_PAGO_PUBLIC_KEY = process.env.REACT_APP_MERCADO_PAGO_PUBLIC_KEY;

const mapContainerStyle = {
  width: '100%',
  height: '600px'
};

const defaultCenter = {
  lat: -23.5505,
  lng: -46.6333
};

const HomePage = () => {
  const [parkingLots, setParkingLots] = useState([]);
  const [selectedLot, setSelectedLot] = useState(null);
  const [userLocation, setUserLocation] = useState(defaultCenter);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const { currentUser, signOut } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  
  // Payment modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedHours, setSelectedHours] = useState(1);
  const [paymentData, setPaymentData] = useState(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('pending'); // pending, success, failed
  const [paymentMethod, setPaymentMethod] = useState('pix'); // 'pix' or 'card'
  const [cardToken, setCardToken] = useState(null);
  const [mpInstance, setMpInstance] = useState(null);
  const [brickController, setBrickController] = useState(null);
  const cardPaymentBrickRef = useRef(null);

  // New pricing system states
  const [pricingType, setPricingType] = useState('hourly'); // 'hourly', 'progressive', 'period', 'daily', 'morning_shift', 'afternoon_shift', 'night_shift'
  const [calculatedTotal, setCalculatedTotal] = useState(0);

  const [guestEmail, setGuestEmail] = useState('');

  // Vehicle Check-in/Check-out states
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showCheckOutModal, setShowCheckOutModal] = useState(false);
  const [checkInData, setCheckInData] = useState({
    license_plate: '',
    driver_name: '',
    vehicle_model: '',
    vehicle_color: ''
  });
  const [checkOutSummary, setCheckOutSummary] = useState(null);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  // Initialize Mercado Pago SDK
  const initializeMercadoPago = useCallback(async (publicKey) => {
    if (!publicKey) {
      console.warn('⚠️ No Public Key provided for MP initialization');
      return;
    }

    if (typeof window.MercadoPago === 'undefined') {
      console.log('⏳ Waiting for Mercado Pago SDK to load...');
      setTimeout(() => initializeMercadoPago(publicKey), 100);
      return;
    }

    try {
      const mp = new window.MercadoPago(publicKey, {
        locale: 'pt-BR'
      });
      setMpInstance(mp);
      console.log(`✅ Mercado Pago SDK initialized with key: ${publicKey.substring(0, 10)}...`);
    } catch (error) {
      console.error('❌ Error initializing Mercado Pago SDK:', error);
    }
  }, []);

  // Initial load with Platform Key
  useEffect(() => {
    initializeMercadoPago(MERCADO_PAGO_PUBLIC_KEY);
  }, [initializeMercadoPago]);

  // Re-initialize when a lot is selected (if it has a specific owner key)
  useEffect(() => {
    if (selectedLot) {
      const keyToUse = selectedLot.owner_public_key || MERCADO_PAGO_PUBLIC_KEY;
      initializeMercadoPago(keyToUse);
    }
  }, [selectedLot, initializeMercadoPago]);

  // Render Card Payment Brick when tab changes to card and MP is ready
  useEffect(() => {
    const renderCardBrick = async () => {
      if (!mpInstance || !showPaymentModal || paymentMethod !== 'card' || !selectedLot) {
        return;
      }

      // Clean up previous brick if exists
      if (brickController) {
        try {
          await brickController.unmount();
        } catch (error) {
          console.log('Brick cleanup:', error);
        }
      }

      try {
        const bricksBuilder = mpInstance.bricks();
        const controller = await bricksBuilder.create('cardPayment', 'cardPaymentBrick', {
          initialization: {
            amount: parseFloat(calculateTotal()),
            payer: {
              email: currentUser?.email || guestEmail || undefined
            }
          },
          callbacks: {
            onReady: () => {
              console.log('✅ Card Payment Brick is ready');
            },
            onSubmit: async (formData) => {
              console.log('Card form submitted:', formData);
              return handleCardPaymentSubmit(formData);
            },
            onError: (error) => {
              console.error('❌ Card Payment Brick error:', error);
              toast.error('Erro no formulário do cartão');
            }
          }
        });
        
        setBrickController(controller);
        console.log('✅ Card Payment Brick rendered');
      } catch (error) {
        console.error('❌ Error rendering Card Payment Brick:', error);
      }
    };

    renderCardBrick();

    // Cleanup on unmount or when dependencies change
    return () => {
      if (brickController) {
        brickController.unmount().catch(err => console.log('Brick unmount:', err));
      }
    };
  }, [mpInstance, showPaymentModal, paymentMethod, selectedLot]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(pos);
          setMapCenter(pos);
        },
        () => {
          console.log('Geolocation failed, using default location');
        }
      );
    }
  }, []);

  const fetchParkingLots = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/parking-lots`);
      setParkingLots(response.data);
    } catch (error) {
      console.error('Error fetching parking lots:', error);
    }
  }, []);

  useEffect(() => {
    fetchParkingLots();
  }, [fetchParkingLots]);

  const getMarkerIcon = (type) => {
    const colors = {
      indoor: '#302681',
      outdoor: '#009440',
      mixed: '#FFCB00'
    };
    return {
      path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
      fillColor: colors[type] || colors.indoor,
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      scale: 10
    };
  };

  const handlePaymentClick = (lot) => {
    setSelectedLot(lot);
    setShowPaymentModal(true);
    setSelectedHours(1);
    setPaymentData(null);
    setShowQRCode(false);
    setPaymentStatus('pending');
    setPaymentMethod('pix');
    setCardToken(null);
    // Reset pricing type based on available options
    const pricing = lot.pricing_config;
    if (pricing?.progressive_enabled) {
      setPricingType('progressive');
    } else {
      setPricingType('hourly');
    }
  };

  const calculateTotal = useCallback(() => {
    if (!selectedLot) return '0.00';
    
    const pricing = selectedLot.pricing_config || {};
    
    switch (pricingType) {
      case 'progressive':
        if (pricing.progressive_enabled) {
          const firstHour = pricing.first_hour_rate || selectedLot.price_per_hour;
          const additional = pricing.additional_hour_rate || selectedLot.price_per_hour;
          if (selectedHours === 1) {
            return firstHour.toFixed(2);
          }
          return (firstHour + (additional * (selectedHours - 1))).toFixed(2);
        }
        break;
      case 'period':
        if (pricing.period_enabled) {
          return (pricing.period_rate || 0).toFixed(2);
        }
        break;
      case 'daily':
        if (pricing.daily_enabled) {
          return (pricing.daily_rate || 0).toFixed(2);
        }
        break;
      case 'morning_shift':
        if (pricing.morning_shift_enabled) {
          return (pricing.morning_shift_rate || 0).toFixed(2);
        }
        break;
      case 'afternoon_shift':
        if (pricing.afternoon_shift_enabled) {
          return (pricing.afternoon_shift_rate || 0).toFixed(2);
        }
        break;
      case 'night_shift':
        if (pricing.night_shift_enabled) {
          return (pricing.night_shift_rate || 0).toFixed(2);
        }
        break;
      default:
        break;
    }
    
    // Default: hourly rate
    return (selectedLot.price_per_hour * selectedHours).toFixed(2);
  }, [selectedLot, selectedHours, pricingType]);

  // Calculate progressive price grid for hours 1-12
  const calculateProgressiveGrid = useCallback(() => {
    if (!selectedLot) return [];
    
    const pricing = selectedLot.pricing_config || {};
    const firstHour = pricing.first_hour_rate || selectedLot.price_per_hour;
    const additional = pricing.additional_hour_rate || selectedLot.price_per_hour;
    
    const grid = [];
    for (let h = 1; h <= 12; h++) {
      const total = h === 1 ? firstHour : firstHour + (additional * (h - 1));
      const savings = h > 1 ? (selectedLot.price_per_hour * h) - total : 0;
      grid.push({ hours: h, total, savings });
    }
    return grid;
  }, [selectedLot]);

  const createPixPayment = async () => {
    if (!selectedLot) {
      toast.error('Selecione um estacionamento');
      return;
    }

    setLoading(true);
    
    try {
      const userEmail = currentUser?.email || guestEmail || 'customer@parkingspot.com';
      
      const paymentPayload = {
        parking_lot_id: selectedLot.id,
        hours: selectedHours,
        user_email: userEmail,
        payment_method: 'pix',
        pricing_type: pricingType,
        calculated_amount: parseFloat(calculateTotal())
      };
      
      const response = await axios.post(`${API}/payments/create`, paymentPayload);

      setPaymentData(response.data);
      setShowQRCode(true);
      toast.success('QR Code gerado! Escaneie para pagar');
      checkPaymentStatus(response.data.id);
    } catch (error) {
      console.error('Error creating PIX payment:', error);
      toast.error(error.response?.data?.detail || 'Erro ao processar pagamento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCardPaymentSubmit = async (formData) => {
    // This function is called by the CardPayment Brick when user submits
    console.log('💳 Card payment submitted:', formData);
    
    if (!selectedLot) {
      toast.error('Selecione um estacionamento');
      return { error_message: 'Nenhum estacionamento selecionado' };
    }

    setLoading(true);
    
    try {
      const userEmail = currentUser?.email || guestEmail || 'customer@parkingspot.com';
      
      const paymentPayload = {
        parking_lot_id: selectedLot.id,
        hours: selectedHours,
        user_email: userEmail,
        payment_method: 'card',
        pricing_type: pricingType,
        calculated_amount: parseFloat(calculateTotal()),
        card_token: formData.token,
        payment_method_id: formData.payment_method_id,
        installments: formData.installments || 1,
        issuer_id: formData.issuer_id,
        payer: formData.payer
      };
      
      console.log('💳 Sending payment to backend:', paymentPayload);
      const response = await axios.post(`${API}/payments/create`, paymentPayload);
      console.log('✅ Payment response:', response.data);

      setPaymentData(response.data);
      setShowQRCode(true);

      if (response.data.status === 'approved') {
        setPaymentStatus('success');
        toast.success('Pagamento aprovado com sucesso! 🎉');
        return { status: 'approved' };
      } else if (response.data.status === 'rejected') {
        setPaymentStatus('failed');
        const detail = response.data.status_detail || '';
        let msg = 'Pagamento recusado pelo banco.';
        if (detail === 'cc_rejected_high_risk') msg += ' (Alto Risco de Fraude - Tente outro cartão)';
        if (detail === 'cc_rejected_insufficient_amount') msg += ' (Saldo Insuficiente)';
        if (detail === 'cc_rejected_bad_filled_other') msg += ' (Dados do cartão incorretos)';
        
        toast.error(msg);
        return { error_message: msg };
      } else {
        setPaymentStatus('pending');
        toast.info('Pagamento em processamento... Aguarde.');
        checkPaymentStatus(response.data.id);
        // Brick expects a promise resolution, but we aren't fully approved yet.
        // We return approved to close the brick, but show pending UI.
        return { status: 'approved' }; 
      }
    } catch (error) {
      console.error('❌ Error creating card payment:', error);
      toast.error(error.response?.data?.detail || 'Erro ao processar pagamento. Tente novamente.');
      setPaymentStatus('failed');
      setLoading(false);
      
      // Return error to Brick (important!)
      return { 
        error_message: error.response?.data?.detail || 'Erro ao processar pagamento'
      };
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (paymentId) => {
    // Poll for payment status every 5 seconds
    const intervalId = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/payments/${paymentId}`);
        const status = response.data.status;
        
        if (status === 'approved') {
          setPaymentStatus('success');
          clearInterval(intervalId);
          toast.success('Pagamento confirmado!');
        } else if (status === 'rejected' || status === 'cancelled') {
          setPaymentStatus('failed');
          clearInterval(intervalId);
          toast.error('Pagamento não aprovado pelo banco');
        } else if (status === 'in_process') {
          // Keep polling but notify user occasionally
          console.log('Payment is in process (review)...');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      }
    }, 5000);

    // Stop checking after 5 minutes
    setTimeout(() => {
      clearInterval(intervalId);
      if (paymentStatus === 'pending') {
        toast.warning('O pagamento ainda está em processamento pelo banco.');
      }
    }, 300000);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Código PIX copiado!');
  };

  // ===== VEHICLE CHECK-IN/OUT FUNCTIONS =====
  
  const handleCheckInClick = (lot) => {
    setSelectedLot(lot);
    setCheckInData({
      license_plate: '',
      driver_name: '',
      vehicle_model: '',
      vehicle_color: ''
    });
    setShowCheckInModal(true);
  };

  const handleCheckOutClick = (lot) => {
    setSelectedLot(lot);
    setCheckOutSummary(null);
    setPaymentComplete(false);
    setReceiptData(null);
    setShowCheckOutModal(true);
  };

  const submitCheckIn = async () => {
    if (!checkInData.license_plate.trim()) {
      toast.error('Por favor, informe a placa do veículo');
      return;
    }

    setCheckInLoading(true);
    try {
      const response = await axios.post(`${API}/vehicles/check-in`, {
        parking_lot_id: selectedLot.id,
        license_plate: checkInData.license_plate.trim().toUpperCase(),
        driver_name: checkInData.driver_name || null,
        vehicle_model: checkInData.vehicle_model || null,
        vehicle_color: checkInData.vehicle_color || null
      });

      toast.success('Entrada registrada com sucesso! 🚗');
      setShowCheckInModal(false);
      fetchParkingLots(); // Atualizar vagas disponíveis
    } catch (error) {
      console.error('Check-in error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao registrar entrada');
    } finally {
      setCheckInLoading(false);
    }
  };

  const lookupVehicle = async () => {
    if (!checkInData.license_plate.trim()) {
      toast.error('Por favor, informe a placa do veículo');
      return;
    }

    setCheckInLoading(true);
    try {
      const response = await axios.post(`${API}/vehicles/check-out-summary`, {
        parking_lot_id: selectedLot.id,
        license_plate: checkInData.license_plate.trim().toUpperCase()
      });

      setCheckOutSummary(response.data);
    } catch (error) {
      console.error('Lookup error:', error);
      toast.error(error.response?.data?.detail || 'Veículo não encontrado');
    } finally {
      setCheckInLoading(false);
    }
  };

  const payForVehicle = async () => {
    if (!checkOutSummary) return;

    setCheckInLoading(true);
    try {
      // Criar pagamento
      const response = await axios.post(`${API}/vehicles/pay`, null, {
        params: {
          check_in_id: checkOutSummary.check_in_id,
          user_email: guestEmail || 'anonimo@parkingspot.com',
          payment_method: paymentMethod
        }
      });

      setPaymentData(response.data.payment);
      setShowQRCode(true);
      
      if (paymentMethod === 'pix') {
        toast.success('QR Code gerado! Escaneie para pagar');
        // Iniciar polling de status
        checkVehiclePaymentStatus(checkOutSummary.check_in_id, response.data.payment.id);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao processar pagamento');
    } finally {
      setCheckInLoading(false);
    }
  };

  const checkVehiclePaymentStatus = async (checkInId, paymentId) => {
    const intervalId = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/payments/${paymentId}`);
        const status = response.data.status;
        
        if (status === 'approved') {
          // Confirmar liberação da vaga
          await axios.post(`${API}/vehicles/confirm-payment/${checkInId}`);
          
          setPaymentStatus('success');
          setPaymentComplete(true);
          setReceiptData({
            parking_lot: checkOutSummary.parking_lot_name,
            license_plate: checkOutSummary.license_plate,
            check_in_time: checkOutSummary.check_in_time,
            check_out_time: new Date().toISOString(),
            hours: checkOutSummary.hours_charged,
            amount: checkOutSummary.total_amount,
            transaction_id: paymentId
          });
          clearInterval(intervalId);
          toast.success('Pagamento confirmado! Vaga liberada 🎉');
          fetchParkingLots();
        } else if (status === 'rejected' || status === 'cancelled') {
          setPaymentStatus('failed');
          clearInterval(intervalId);
          toast.error('Pagamento não aprovado');
        }
      } catch (error) {
        console.error('Status check error:', error);
      }
    }, 5000);

    // Parar após 5 minutos
    setTimeout(() => {
      clearInterval(intervalId);
    }, 300000);
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins} min`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}min`;
  };

  const handleLogout = async () => {
    await signOut();
    setMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <MapPin className="w-8 h-8" style={{ color: '#302681' }} />
              <h1 className="text-2xl font-bold" style={{ color: '#302681' }}>ParkingSpot</h1>
            </div>
            
            <nav className="hidden md:flex space-x-6">
              <a href="/como-funciona" className="text-gray-700 hover:text-[#302681] transition">Como Funciona</a>
              <a href="/precos" className="text-gray-700 hover:text-[#302681] transition">Preços</a>
              <a href="/ajuda" className="text-gray-700 hover:text-[#302681] transition">Ajuda</a>
              <a href="/demo" className="text-gray-700 hover:text-[#302681] transition">Demo</a>
            </nav>

            <div className="flex items-center space-x-4">
              {currentUser ? (
                <div className="relative">
                  <button
                    onClick={() => setMenuOpen(!menuOpen)}
                    className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-100 transition"
                    data-testid="user-menu-button"
                  >
                    <User className="w-5 h-5" style={{ color: '#302681' }} />
                    <span className="text-sm">{currentUser.email}</span>
                  </button>
                  {menuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2">
                      <button
                        onClick={() => navigate('/dashboard')}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100"
                        data-testid="dashboard-link"
                      >
                        Meu Dashboard
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 text-red-600 flex items-center space-x-2"
                        data-testid="logout-button"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Sair</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => navigate('/auth')}
                  className="px-6 py-2 rounded-lg text-white font-medium transition-all"
                  style={{ backgroundColor: '#302681' }}
                  data-testid="login-button"
                >
                  Entrar / Cadastrar
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#302681' }}>
            Encontre Vagas de Estacionamento
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Rápido, fácil e econômico. Veja vagas disponíveis em tempo real.
          </p>
        </div>
      </section>

      {/* Map Section */}
      <section className="max-w-7xl mx-auto px-4 pb-12">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="p-4 bg-gradient-to-r from-[#302681] to-[#831D1C]">
            <h3 className="text-white text-xl font-semibold flex items-center">
              <Navigation className="w-5 h-5 mr-2" />
              Estacionamentos Próximos
            </h3>
          </div>
          <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={mapCenter}
              zoom={13}
              data-testid="google-map"
            >
              {/* User location marker */}
              <Marker
                position={userLocation}
                icon={{
                  path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                  fillColor: '#4285F4',
                  fillOpacity: 1,
                  strokeColor: '#ffffff',
                  strokeWeight: 3,
                  scale: 8
                }}
              />

              {/* Parking lot markers */}
              {parkingLots.map((lot) => (
                <Marker
                  key={lot.id}
                  position={{ lat: lot.latitude, lng: lot.longitude }}
                  onClick={() => setSelectedLot(lot)}
                  icon={getMarkerIcon(lot.parking_type)}
                />
              ))}

              {/* Info Window */}
              {selectedLot && (
                <InfoWindow
                  position={{ lat: selectedLot.latitude, lng: selectedLot.longitude }}
                  onCloseClick={() => setSelectedLot(null)}
                >
                  <div className="p-2" style={{ minWidth: '250px', maxWidth: '300px' }}>
                    {selectedLot.photo_url && (
                      <img 
                        src={selectedLot.photo_url} 
                        alt={selectedLot.name}
                        className="w-full h-32 object-cover rounded mb-2"
                      />
                    )}
                    <h3 className="font-bold text-lg mb-1">{selectedLot.name}</h3>
                    <p className="text-sm text-gray-600 mb-1">{selectedLot.address}</p>
                    <a 
                      href={`https://www.google.com/maps/dir/?api=1&destination=${selectedLot.latitude},${selectedLot.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border-2 border-[#800020] text-[#800020] hover:bg-[#800020] hover:text-white transition-colors mb-2"
                    >
                      📍 Como chegar
                    </a>
                    {selectedLot.description && (
                      <p className="text-sm text-gray-700 italic mb-2 bg-gray-50 p-2 rounded">
                        &ldquo;{selectedLot.description}&rdquo;
                      </p>
                    )}
                    <div className="space-y-1 text-sm">
                      <p><strong>Vagas Disponíveis:</strong> {selectedLot.available_spots}/{selectedLot.total_spots}</p>
                      <p><strong>Preço:</strong> R$ {selectedLot.price_per_hour.toFixed(2)}/hora</p>
                      <p><strong>Horário:</strong> {selectedLot.schedule}</p>
                      <div className="mt-2">
                        <span 
                          className="inline-block px-3 py-1 rounded-full text-xs text-white font-medium"
                          style={{
                            backgroundColor: 
                              selectedLot.parking_type === 'indoor' ? '#302681' :
                              selectedLot.parking_type === 'outdoor' ? '#009440' : '#FFCB00'
                          }}
                        >
                          {selectedLot.parking_type === 'indoor' ? 'Coberto' :
                           selectedLot.parking_type === 'outdoor' ? 'Descoberto' : 'Misto'}
                        </span>
                      </div>
                      
                      {/* Botões de Check-in/Check-out */}
                      <div className="mt-3 space-y-2">
                        {selectedLot.available_spots > 0 ? (
                          <button
                            onClick={() => handleCheckInClick(selectedLot)}
                            className="w-full px-4 py-2 rounded-lg text-white font-medium flex items-center justify-center space-x-2 hover:opacity-90 transition"
                            style={{ backgroundColor: '#009440' }}
                            data-testid="check-in-button"
                          >
                            <Car className="w-4 h-4" />
                            <span>Estou Estacionado Aqui</span>
                          </button>
                        ) : (
                          <div className="w-full px-4 py-2 rounded-lg bg-red-100 text-red-700 font-medium text-center">
                            🚫 LOTADO
                          </div>
                        )}
                        
                        <button
                          onClick={() => handleCheckOutClick(selectedLot)}
                          className="w-full px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2 hover:opacity-90 transition border-2 border-[#302681] text-[#302681] hover:bg-[#302681] hover:text-white"
                          data-testid="check-out-button"
                        >
                          <Clock className="w-4 h-4" />
                          <span>Já Estou Estacionado</span>
                        </button>
                        
                        <button
                          onClick={() => handlePaymentClick(selectedLot)}
                          className="w-full px-4 py-2 rounded-lg text-white font-medium flex items-center justify-center space-x-2 hover:opacity-90 transition text-sm"
                          style={{ backgroundColor: '#831D1C' }}
                        >
                          <CreditCard className="w-4 h-4" />
                          <span>Pagar Antecipado</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          </LoadScript>
        </div>

        {/* Legend */}
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <h4 className="font-semibold mb-3">Legenda:</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-3">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#302681' }}></div>
              <span>Coberto (Indoor)</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#009440' }}></div>
              <span>Descoberto (Outdoor)</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#FFCB00' }}></div>
              <span>Misto</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {showQRCode ? (paymentMethod === 'pix' ? 'Escaneie o QR Code' : 'Processamento do Pagamento') : 'Realizar Pagamento'}
            </DialogTitle>
          </DialogHeader>

          {!showQRCode ? (
            <div className="space-y-6">
              {/* Parking Lot Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg mb-1">{selectedLot?.name}</h3>
                <p className="text-sm text-gray-600">{selectedLot?.address}</p>
                {selectedLot?.description && (
                  <p className="text-sm text-gray-700 italic mt-2 pt-2 border-t border-gray-200">
                    &ldquo;{selectedLot.description}&rdquo;
                  </p>
                )}
              </div>

              {/* Escolha como deseja pagar */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Escolha como deseja pagar:</h4>

                {/* Option: Por Hora (Progressive or Regular) */}
                <div 
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    pricingType === 'progressive' || pricingType === 'hourly'
                      ? 'border-[#302681] bg-[#302681]/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setPricingType(selectedLot?.pricing_config?.progressive_enabled ? 'progressive' : 'hourly')}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        pricingType === 'progressive' || pricingType === 'hourly'
                          ? 'border-[#302681]'
                          : 'border-gray-300'
                      }`}>
                        {(pricingType === 'progressive' || pricingType === 'hourly') && (
                          <div className="w-3 h-3 rounded-full bg-[#302681]"></div>
                        )}
                      </div>
                      <div>
                        <p className="font-semibold">Por Hora</p>
                        {selectedLot?.pricing_config?.progressive_enabled ? (
                          <p className="text-xs text-gray-500">
                            1ª hora R$ {selectedLot.pricing_config.first_hour_rate?.toFixed(2)} | Adicionais R$ {selectedLot.pricing_config.additional_hour_rate?.toFixed(2)}
                          </p>
                        ) : (
                          <p className="text-xs text-gray-500">
                            R$ {selectedLot?.price_per_hour?.toFixed(2)}/hora
                          </p>
                        )}
                      </div>
                    </div>
                    <span className="text-lg font-bold text-[#302681]">
                      R$ {selectedLot?.pricing_config?.progressive_enabled 
                        ? selectedLot.pricing_config.first_hour_rate?.toFixed(2)
                        : selectedLot?.price_per_hour?.toFixed(2)}
                    </span>
                  </div>

                  {/* Progressive Discount Badge */}
                  {selectedLot?.pricing_config?.progressive_enabled && (
                    <div className="bg-green-50 border border-green-200 rounded p-2 mb-3">
                      <p className="text-xs text-green-700">
                        <span className="font-semibold">⚡ Desconto Progressivo:</span> 1ª hora R$ {selectedLot.pricing_config.first_hour_rate?.toFixed(2)} | Cada hora adicional R$ {selectedLot.pricing_config.additional_hour_rate?.toFixed(2)}
                      </p>
                    </div>
                  )}

                  {/* Hours Grid - Only show when Por Hora is selected */}
                  {(pricingType === 'progressive' || pricingType === 'hourly') && (
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Selecione de 1 a 12 horas:</p>
                      <div className="grid grid-cols-4 gap-2">
                        {(selectedLot?.pricing_config?.progressive_enabled ? calculateProgressiveGrid() : []).length > 0 
                          ? calculateProgressiveGrid().map(({ hours, total, savings }) => (
                            <button
                              key={hours}
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setSelectedHours(hours); }}
                              className={`p-2 rounded-lg border text-center transition-all ${
                                selectedHours === hours
                                  ? 'border-[#302681] bg-[#302681] text-white'
                                  : 'border-gray-200 hover:border-[#302681] bg-white'
                              }`}
                            >
                              <div className="text-sm font-bold">{hours}h</div>
                              <div className="text-xs">R$ {total.toFixed(0)}</div>
                              {savings > 0 && (
                                <div className={`text-[10px] ${selectedHours === hours ? 'text-green-200' : 'text-green-600'}`}>
                                  -R$ {savings.toFixed(0)}
                                </div>
                              )}
                            </button>
                          ))
                          : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((hours) => (
                            <button
                              key={hours}
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setSelectedHours(hours); }}
                              className={`p-2 rounded-lg border text-center transition-all ${
                                selectedHours === hours
                                  ? 'border-[#302681] bg-[#302681] text-white'
                                  : 'border-gray-200 hover:border-[#302681] bg-white'
                              }`}
                            >
                              <div className="text-sm font-bold">{hours}h</div>
                              <div className="text-xs">R$ {(selectedLot?.price_per_hour * hours).toFixed(0)}</div>
                            </button>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>

                {/* Option: Período (if enabled) */}
                {selectedLot?.pricing_config?.period_enabled && (
                  <div 
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      pricingType === 'period'
                        ? 'border-[#302681] bg-[#302681]/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setPricingType('period')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          pricingType === 'period' ? 'border-[#302681]' : 'border-gray-300'
                        }`}>
                          {pricingType === 'period' && <div className="w-3 h-3 rounded-full bg-[#302681]"></div>}
                        </div>
                        <div>
                          <p className="font-semibold">Período</p>
                          <p className="text-xs text-gray-500">Valor fixo - Economize!</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-bold text-[#302681]">R$ {selectedLot.pricing_config.period_rate?.toFixed(2)}</span>
                        <p className="text-xs text-gray-500">valor fixo</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Option: Diária Completa (if enabled) */}
                {selectedLot?.pricing_config?.daily_enabled && (
                  <div 
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      pricingType === 'daily'
                        ? 'border-[#302681] bg-[#302681]/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setPricingType('daily')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          pricingType === 'daily' ? 'border-[#302681]' : 'border-gray-300'
                        }`}>
                          {pricingType === 'daily' && <div className="w-3 h-3 rounded-full bg-[#302681]"></div>}
                        </div>
                        <div>
                          <p className="font-semibold">Diária Completa</p>
                          <p className="text-xs text-gray-500">Fique o dia todo (24h)</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-bold text-[#302681]">R$ {selectedLot.pricing_config.daily_rate?.toFixed(2)}</span>
                        <p className="text-xs text-gray-500">24 horas</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Option: Turno Manhã (if enabled) */}
                {selectedLot?.pricing_config?.morning_shift_enabled && (
                  <div 
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      pricingType === 'morning_shift'
                        ? 'border-[#302681] bg-[#302681]/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setPricingType('morning_shift')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          pricingType === 'morning_shift' ? 'border-[#302681]' : 'border-gray-300'
                        }`}>
                          {pricingType === 'morning_shift' && <div className="w-3 h-3 rounded-full bg-[#302681]"></div>}
                        </div>
                        <div>
                          <p className="font-semibold">Turno Manhã (6h-12h)</p>
                          <p className="text-xs text-gray-500">6 horas no período da manhã</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-bold text-[#302681]">R$ {selectedLot.pricing_config.morning_shift_rate?.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Option: Turno Tarde (if enabled) */}
                {selectedLot?.pricing_config?.afternoon_shift_enabled && (
                  <div 
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      pricingType === 'afternoon_shift'
                        ? 'border-[#302681] bg-[#302681]/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setPricingType('afternoon_shift')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          pricingType === 'afternoon_shift' ? 'border-[#302681]' : 'border-gray-300'
                        }`}>
                          {pricingType === 'afternoon_shift' && <div className="w-3 h-3 rounded-full bg-[#302681]"></div>}
                        </div>
                        <div>
                          <p className="font-semibold">Turno Tarde (12h-18h)</p>
                          <p className="text-xs text-gray-500">6 horas no período da tarde</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-bold text-[#302681]">R$ {selectedLot.pricing_config.afternoon_shift_rate?.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Option: Turno Noite (if enabled) */}
                {selectedLot?.pricing_config?.night_shift_enabled && (
                  <div 
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      pricingType === 'night_shift'
                        ? 'border-[#302681] bg-[#302681]/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setPricingType('night_shift')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          pricingType === 'night_shift' ? 'border-[#302681]' : 'border-gray-300'
                        }`}>
                          {pricingType === 'night_shift' && <div className="w-3 h-3 rounded-full bg-[#302681]"></div>}
                        </div>
                        <div>
                          <p className="font-semibold">Turno Noite (18h-6h)</p>
                          <p className="text-xs text-gray-500">12 horas no período noturno</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-bold text-[#302681]">R$ {selectedLot.pricing_config.night_shift_rate?.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Guest Email Input */}
              {!currentUser && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Seu Email (Opcional)
                  </label>
                  <p className="text-xs text-gray-500 mb-2">Para receber o comprovante do pagamento</p>
                  <input
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder="exemplo@email.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#302681] focus:border-transparent"
                  />
                </div>
              )}

              {/* Total Amount */}
              <div className="bg-gradient-to-r from-[#302681] to-[#831D1C] text-white p-6 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm opacity-90">Total a pagar</p>
                    <p className="text-3xl font-bold">R$ {calculateTotal()}</p>
                  </div>
                  <div className="text-right text-sm opacity-90">
                    {(pricingType === 'hourly' || pricingType === 'progressive') && (
                      <p>{selectedHours} hora{selectedHours > 1 ? 's' : ''}</p>
                    )}
                    {pricingType === 'period' && <p>Período</p>}
                    {pricingType === 'daily' && <p>Diária (24h)</p>}
                    {pricingType === 'morning_shift' && <p>Manhã (6h-12h)</p>}
                    {pricingType === 'afternoon_shift' && <p>Tarde (12h-18h)</p>}
                    {pricingType === 'night_shift' && <p>Noite (18h-6h)</p>}
                  </div>
                </div>
              </div>

              {/* Payment Method Tabs - Changed button text to just "Pagar" */}
              <Tabs defaultValue="pix" className="w-full" onValueChange={(value) => setPaymentMethod(value)}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="pix">PIX</TabsTrigger>
                  <TabsTrigger value="card">Cartão</TabsTrigger>
                </TabsList>

                {/* PIX Tab */}
                <TabsContent value="pix" className="space-y-4">
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                    <p className="text-sm text-blue-800">
                      Pagamento instantâneo via PIX. Você receberá um QR Code para escanear.
                    </p>
                  </div>
                  <Button
                    onClick={createPixPayment}
                    disabled={loading}
                    className="w-full text-white py-6 text-lg font-semibold"
                    style={{ backgroundColor: '#302681' }}
                  >
                    {loading ? 'Gerando...' : 'Pagar'}
                  </Button>
                </TabsContent>

                {/* Card Tab */}
                <TabsContent value="card" className="space-y-4">
                  <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
                    <p className="text-sm text-green-800">
                      Pagamento seguro com cartão de crédito ou débito.
                    </p>
                  </div>
                  {!MERCADO_PAGO_PUBLIC_KEY && (
                    <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                      <p className="text-sm text-red-800">
                        ⚠️ Chave pública do Mercado Pago não configurada.
                      </p>
                    </div>
                  )}
                  {!mpInstance && MERCADO_PAGO_PUBLIC_KEY && (
                    <div className="text-center text-gray-500 py-8">
                      ⏳ Carregando formulário de pagamento...
                    </div>
                  )}
                  {/* Container where the Card Payment Brick will be rendered */}
                  <div id="cardPaymentBrick" ref={cardPaymentBrickRef}></div>
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            // QR Code Display Screen
            <div className="space-y-6">
              {paymentStatus === 'pending' && (
                <>
                  <div className="text-center">
                    {paymentMethod === 'pix' ? (
                      <div className="bg-white p-6 rounded-lg border-2 border-gray-200 inline-block">
                        {paymentData?.qr_code_base64 ? (
                          <img
                            src={`data:image/png;base64,${paymentData.qr_code_base64}`}
                            alt="QR Code PIX"
                            className="w-64 h-64"
                          />
                        ) : (
                          <div className="w-64 h-64 bg-gray-100 flex items-center justify-center">
                            <p className="text-gray-500">QR Code não disponível</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="py-12 flex flex-col items-center justify-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#302681] mb-4"></div>
                        <h3 className="text-xl font-semibold text-gray-700">Processando pagamento...</h3>
                        <p className="text-sm text-gray-500 mt-2">Estamos confirmando a transação com seu banco.</p>
                      </div>
                    )}
                  </div>

                  {paymentMethod === 'pix' && (
                    <>
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                        <h4 className="font-semibold text-blue-900 mb-2">Como pagar:</h4>
                        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                          <li>Abra o app do seu banco</li>
                          <li>Escolha pagar com PIX</li>
                          <li>Escaneie o QR Code acima</li>
                          <li>Confirme o pagamento</li>
                        </ol>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-xs text-gray-600 mb-2">Ou copie o código PIX:</p>
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={paymentData?.qr_code || ''}
                            readOnly
                            className="flex-1 px-3 py-2 border rounded text-xs bg-white"
                          />
                          <button
                            onClick={() => copyToClipboard(paymentData?.qr_code)}
                            className="px-4 py-2 bg-[#302681] text-white rounded hover:opacity-90 flex items-center space-x-2"
                          >
                            <Copy className="w-4 h-4" />
                            <span>Copiar</span>
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded">
                    <p className="text-sm text-amber-800">
                      ⏱️ Aguardando confirmação do pagamento...
                    </p>
                  </div>

                  <div className="text-center text-sm text-gray-600">
                    <p className="font-semibold">Valor: R$ {paymentData?.amount.toFixed(2)}</p>
                    <p className="text-xs mt-1">ID do Pagamento: {paymentData?.mercado_pago_id}</p>
                  </div>
                </>
              )}

              {paymentStatus === 'success' && (
                <div className="text-center space-y-4">
                  <CheckCircle className="w-20 h-20 text-green-500 mx-auto" />
                  <h3 className="text-2xl font-bold text-green-700">Pagamento Confirmado!</h3>
                  <p className="text-gray-600">
                    Seu pagamento foi aprovado com sucesso. Você pode estacionar agora!
                  </p>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="font-semibold">Detalhes do Estacionamento:</p>
                    <p className="text-sm mt-2">{selectedLot?.name}</p>
                    <p className="text-sm">{selectedHours} hora{selectedHours > 1 ? 's' : ''}</p>
                    <p className="text-sm font-bold">R$ {calculateTotal()}</p>
                  </div>
                  <Button
                    onClick={() => {
                      setShowPaymentModal(false);
                      setShowQRCode(false);
                      setPaymentData(null);
                    }}
                    className="w-full"
                    style={{ backgroundColor: '#302681' }}
                  >
                    Fechar
                  </Button>
                </div>
              )}

              {paymentStatus === 'failed' && (
                <div className="text-center space-y-4">
                  <XCircle className="w-20 h-20 text-red-500 mx-auto" />
                  <h3 className="text-2xl font-bold text-red-700">Pagamento Não Aprovado</h3>
                  <p className="text-gray-600">
                    O pagamento não foi confirmado. Por favor, tente novamente.
                  </p>
                  <Button
                    onClick={() => {
                      setShowQRCode(false);
                      setPaymentData(null);
                      setPaymentStatus('pending');
                    }}
                    className="w-full"
                    style={{ backgroundColor: '#302681' }}
                  >
                    Tentar Novamente
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Check-In Modal */}
      <Dialog open={showCheckInModal} onOpenChange={setShowCheckInModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Car className="w-5 h-5 text-green-600" />
              <span>Registrar Entrada</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Info do Estacionamento */}
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800">{selectedLot?.name}</h3>
              <p className="text-sm text-green-700">{selectedLot?.address}</p>
              <p className="text-sm text-green-600 mt-1">
                Vagas: {selectedLot?.available_spots}/{selectedLot?.total_spots}
              </p>
            </div>

            {/* Formulário */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">
                Placa do Veículo <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={checkInData.license_plate}
                onChange={(e) => setCheckInData({...checkInData, license_plate: e.target.value.toUpperCase()})}
                placeholder="ABC-1234"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg font-mono uppercase"
                maxLength={8}
                data-testid="checkin-plate-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">
                Seu Nome <span className="text-gray-400">(opcional)</span>
              </label>
              <input
                type="text"
                value={checkInData.driver_name}
                onChange={(e) => setCheckInData({...checkInData, driver_name: e.target.value})}
                placeholder="João Silva"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                data-testid="checkin-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Veículo <span className="text-gray-400">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={checkInData.vehicle_model}
                  onChange={(e) => setCheckInData({...checkInData, vehicle_model: e.target.value})}
                  placeholder="Ex: Civic, Gol"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  data-testid="checkin-model-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Cor <span className="text-gray-400">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={checkInData.vehicle_color}
                  onChange={(e) => setCheckInData({...checkInData, vehicle_color: e.target.value})}
                  placeholder="Ex: Prata, Preto"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  data-testid="checkin-color-input"
                />
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
              <p className="text-sm text-blue-800">
                💡 A hora de entrada será registrada automaticamente. Quando for sair, clique em "Já Estou Estacionado" para ver o valor e pagar.
              </p>
            </div>

            <Button
              onClick={submitCheckIn}
              disabled={checkInLoading || !checkInData.license_plate.trim()}
              className="w-full py-6 text-lg font-semibold text-white"
              style={{ backgroundColor: '#009440' }}
              data-testid="confirm-checkin-button"
            >
              {checkInLoading ? 'Registrando...' : '✓ Confirmar Entrada'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Check-Out / Payment Modal */}
      <Dialog open={showCheckOutModal} onOpenChange={setShowCheckOutModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Clock className="w-5 h-5 text-[#302681]" />
              <span>{paymentComplete ? 'Comprovante' : checkOutSummary ? 'Resumo do Estacionamento' : 'Consultar Veículo'}</span>
            </DialogTitle>
          </DialogHeader>

          {/* Estado 1: Buscar Veículo */}
          {!checkOutSummary && !paymentComplete && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold">{selectedLot?.name}</h3>
                <p className="text-sm text-gray-600">{selectedLot?.address}</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Informe a Placa do Veículo
                </label>
                <input
                  type="text"
                  value={checkInData.license_plate}
                  onChange={(e) => setCheckInData({...checkInData, license_plate: e.target.value.toUpperCase()})}
                  placeholder="ABC-1234"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#302681] focus:border-transparent text-lg font-mono uppercase"
                  maxLength={8}
                  data-testid="checkout-plate-input"
                />
              </div>

              <Button
                onClick={lookupVehicle}
                disabled={checkInLoading || !checkInData.license_plate.trim()}
                className="w-full py-4 text-white"
                style={{ backgroundColor: '#302681' }}
                data-testid="lookup-vehicle-button"
              >
                {checkInLoading ? 'Buscando...' : '🔍 Buscar Veículo'}
              </Button>
            </div>
          )}

          {/* Estado 2: Resumo e Pagamento */}
          {checkOutSummary && !paymentComplete && !showQRCode && (
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-[#302681] to-[#831D1C] text-white p-4 rounded-lg">
                <div className="text-center">
                  <p className="text-sm opacity-90">Valor a Pagar</p>
                  <p className="text-4xl font-bold">R$ {checkOutSummary.total_amount.toFixed(2)}</p>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Placa:</span>
                  <span className="font-mono font-bold">{checkOutSummary.license_plate}</span>
                </div>
                {checkOutSummary.driver_name && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Nome:</span>
                    <span>{checkOutSummary.driver_name}</span>
                  </div>
                )}
                {checkOutSummary.vehicle_model && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Veículo:</span>
                    <span>{checkOutSummary.vehicle_model} {checkOutSummary.vehicle_color && `(${checkOutSummary.vehicle_color})`}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Entrada:</span>
                  <span>{new Date(checkOutSummary.check_in_time).toLocaleString('pt-BR')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tempo:</span>
                  <span className="font-semibold">{formatDuration(checkOutSummary.duration_minutes)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Horas cobradas:</span>
                  <span>{checkOutSummary.hours_charged}h</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Email para Comprovante <span className="text-gray-400">(opcional)</span>
                </label>
                <input
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  placeholder="seu@email.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#302681] focus:border-transparent"
                />
              </div>

              {/* Método de Pagamento */}
              <Tabs defaultValue="pix" onValueChange={(value) => setPaymentMethod(value)}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="pix">PIX</TabsTrigger>
                  <TabsTrigger value="card">Cartão</TabsTrigger>
                </TabsList>

                <TabsContent value="pix">
                  <Button
                    onClick={payForVehicle}
                    disabled={checkInLoading}
                    className="w-full py-6 text-lg font-semibold text-white"
                    style={{ backgroundColor: '#302681' }}
                    data-testid="pay-pix-button"
                  >
                    {checkInLoading ? 'Gerando QR Code...' : '💳 Pagar com PIX'}
                  </Button>
                </TabsContent>

                <TabsContent value="card">
                  <Button
                    onClick={payForVehicle}
                    disabled={checkInLoading}
                    className="w-full py-6 text-lg font-semibold text-white"
                    style={{ backgroundColor: '#302681' }}
                    data-testid="pay-card-button"
                  >
                    {checkInLoading ? 'Processando...' : '💳 Pagar com Cartão'}
                  </Button>
                </TabsContent>
              </Tabs>
            </div>
          )}

          {/* Estado 3: QR Code PIX */}
          {checkOutSummary && showQRCode && !paymentComplete && (
            <div className="space-y-4">
              {paymentMethod === 'pix' && paymentData?.qr_code_base64 && (
                <div className="text-center">
                  <div className="bg-white p-4 rounded-lg border-2 border-gray-200 inline-block">
                    <img
                      src={`data:image/png;base64,${paymentData.qr_code_base64}`}
                      alt="QR Code PIX"
                      className="w-56 h-56"
                    />
                  </div>
                  
                  <div className="mt-4 bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-600 mb-2">Ou copie o código:</p>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={paymentData?.qr_code || ''}
                        readOnly
                        className="flex-1 px-2 py-1 border rounded text-xs bg-white"
                      />
                      <button
                        onClick={() => copyToClipboard(paymentData?.qr_code)}
                        className="px-3 py-1 bg-[#302681] text-white rounded text-sm"
                      >
                        Copiar
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 bg-amber-50 border-l-4 border-amber-400 p-3 rounded text-left">
                    <p className="text-sm text-amber-800">
                      ⏱️ Aguardando pagamento... O sistema irá liberar a vaga automaticamente.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Estado 4: Comprovante */}
          {paymentComplete && receiptData && (
            <div className="space-y-4">
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-2" />
                <h3 className="text-xl font-bold text-green-700">Pagamento Confirmado!</h3>
                <p className="text-gray-600">Vaga liberada com sucesso</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg space-y-2 border-2 border-dashed border-gray-300">
                <div className="text-center border-b pb-2 mb-2">
                  <p className="font-bold text-lg">{receiptData.parking_lot}</p>
                  <p className="text-xs text-gray-500">COMPROVANTE DE PAGAMENTO</p>
                </div>
                
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Placa:</span>
                    <span className="font-mono font-bold">{receiptData.license_plate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Entrada:</span>
                    <span>{new Date(receiptData.check_in_time).toLocaleString('pt-BR')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saída:</span>
                    <span>{new Date(receiptData.check_out_time).toLocaleString('pt-BR')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tempo:</span>
                    <span>{receiptData.hours}h</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 mt-2">
                    <span className="font-bold">Total Pago:</span>
                    <span className="font-bold text-lg">R$ {receiptData.amount.toFixed(2)}</span>
                  </div>
                  <div className="text-center text-xs text-gray-500 pt-2">
                    ID: {receiptData.transaction_id}
                  </div>
                </div>
              </div>

              <Button
                onClick={() => {
                  setShowCheckOutModal(false);
                  setCheckOutSummary(null);
                  setPaymentComplete(false);
                  setReceiptData(null);
                  setShowQRCode(false);
                  setPaymentData(null);
                }}
                className="w-full text-white"
                style={{ backgroundColor: '#302681' }}
              >
                Fechar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePage;
